'use client';
import { useState, useEffect } from 'react';
import { getPostsBySection, getAuthors, getPostsByCategory } from '@/lib/api';
import { Story, Author } from '@/lib/types';
import Label from '@/components/ui/Label';
import SectionHeader from '@/components/ui/SectionHeader';
import StoryCard from '@/components/ui/StoryCard';
import Link from 'next/link';

export default function OpinionPage() {
  const [opinionStories, setOpinionStories] = useState<Story[]>([]);
  const [analysisPieces, setAnalysisPieces] = useState<Story[]>([]);
  const [explainerPieces, setExplainerPieces] = useState<Story[]>([]);
  const [allAuthors, setAllAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [opinions, authors] = await Promise.all([
          getPostsBySection('Opinion'),
          getAuthors()
        ]);
        setOpinionStories(opinions);
        setAllAuthors(authors);
        
        // Fetch analysis and explainers (using sections as labels for now if needed or separate fetch)
        const [analysis, explainers] = await Promise.all([
          getPostsByCategory('analysis'), // Assuming analysis is a category or we need to filter
          getPostsByCategory('explainer')
        ]);
        setAnalysisPieces(analysis);
        setExplainerPieces(explainers);

      } catch (error) {
        console.error('Error loading opinion data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const leadOpinion = opinionStories[0];
  const gridOpinions = opinionStories.slice(0, 6);

  return (
    <div className="max-w-[1400px] mx-auto px-5 py-8">

      {/* PAGE HEADER */}
      <div className="mb-8">
        <h1 className="font-['Playfair_Display',Georgia,serif] font-black text-[44px] border-b-[3px] border-[#C8102E] pb-4 mb-3">
          Opinion & Analysis
        </h1>
        <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B]">
          The views expressed in opinion articles are those of the authors and do not necessarily reflect the editorial position of Veritas News.
        </p>
      </div>

      {/* LEAD OPINION */}
      {leadOpinion && (
        <Link href={`/article/${leadOpinion.slug}`} className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-0 border border-[#D4D4D4] mb-10 hover:opacity-80 transition-opacity cursor-pointer group block">
          {/* Text left */}
          <div className="px-8 py-7">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-14 h-14 circle-avatar ${leadOpinion.authorDetails?.avatarBg || 'bg-gray-100'} flex items-center justify-center shrink-0`}>
                <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[18px] text-[#6B6B6B]">{leadOpinion.authorDetails?.initials || leadOpinion.author.charAt(0)}</span>
              </div>
              <div>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[15px]">{leadOpinion.author}</p>
                <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{leadOpinion.authorDetails?.role || 'Contributor'}</p>
              </div>
            </div>
            <Label type="opinion" />
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[28px] lg:text-[32px] leading-[1.2] mt-3 mb-3 group-hover:text-[#1A4A8A] transition-colors">
              {leadOpinion.title}
            </h2>
            <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.55] text-[#2D2D2D]">
              {leadOpinion.deck}
            </p>
          </div>
          {/* Image right */}
          <div 
            className={`${leadOpinion.imageBg?.startsWith('bg-') ? leadOpinion.imageBg : 'bg-gray-100'} h-[240px] lg:h-auto bg-cover bg-center bg-no-repeat`} 
            style={!leadOpinion.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${leadOpinion.imageBg})` } : {}}
          />
        </Link>
      )}

      {/* FEATURED CONTRIBUTORS */}
      <div className="border-t-[2.5px] border-[#0D0D0D] pt-4 mb-8">
        <SectionHeader title="Featured Contributors" />
        <div className="flex flex-wrap gap-6 lg:gap-8">
          {allAuthors.slice(0, 8).map(author => (
            <Link
              key={author.slug}
              href={`/author/${author.slug}`}
              className="text-center group cursor-pointer hover:opacity-80 transition-opacity"
            >
              <div className={`w-[88px] h-[88px] circle-avatar ${author.avatarBg} mx-auto mb-3 flex items-center justify-center group-hover:ring-2 ring-[#C8102E] transition-all`}>
                <span className="font-['Playfair_Display',Georgia,serif] font-bold text-[28px] text-[#6B6B6B]">{author.initials}</span>
              </div>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[14px] text-[#0D0D0D]">{author.name.split(' ').pop()}</p>
              <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] max-w-[100px] mx-auto leading-tight mt-0.5">{author.beat}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* OPINION GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 mb-10">
        {gridOpinions.map((story) => {
          const author = story.authorDetails;
          return (
            <Link key={story.id} href={`/article/${story.slug}`} className="block group hover:opacity-80 transition-opacity cursor-pointer border-t-[3px] border-[#1A4A8A] pt-4">
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-[52px] h-[52px] circle-avatar ${author?.avatarBg || 'bg-gray-100'} shrink-0 flex items-center justify-center`}>
                  <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[16px] text-[#6B6B6B]">{author?.initials || story.author.charAt(0)}</span>
                </div>
                <div>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[15px]">{story.author}</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{author?.role || 'Contributor'}</p>
                </div>
              </div>
              <div className="mb-3"><Label type="opinion" /></div>
              <h3 className="font-['Playfair_Display',Georgia,serif] font-bold text-[18px] leading-[1.3] mb-2 group-hover:text-[#1A4A8A] transition-colors">
                {story.title}
              </h3>
              <p className="font-['Source_Serif_4',Georgia,serif] text-[14px] text-[#2D2D2D] mt-2 line-clamp-2 leading-[1.55]">
                {story.deck}
              </p>
            </Link>
          );
        })}
      </div>

      {/* ANALYSIS SECTION */}
      <section className="bg-[#F5F4F0] py-9 -mx-5 px-5 border-t border-b border-[#D4D4D4] mb-8">
        <SectionHeader title="Analysis" moreLink="/latest" moreLabel="All Analysis →" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {analysisPieces.map(story => <StoryCard key={story.id} story={story} size="medium" />)}
        </div>
      </section>

      {/* EXPLAINERS */}
      <section className="py-8">
        <SectionHeader title="Explainers" moreLink="/latest" moreLabel="All Explainers →" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {explainerPieces.map(story => <StoryCard key={story.id} story={story} size="medium" />)}
        </div>
      </section>

    </div>
  );
}
